import { api } from './index';

import type {
  DataImportRecord,
  DataImportTypesResponse,
} from '@/types/dataImport';

interface CreateCsvPayload {
  data_import: {
    import_type: string;
    source_type: 'csv';
    csv_text: string;
    options?: { dry_run?: boolean };
  };
}

interface CreateGooglePayload {
  data_import: {
    import_type: string;
    source_type: 'google_sheet';
    google_spreadsheet_id: string;
    google_range: string;
    options?: { dry_run?: boolean };
  };
}

export const dataImportsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDataImportTypes: build.query<DataImportTypesResponse, void>({
      query: () => ({ url: '/data_imports/types' }),
      providesTags: ['DataImport'],
    }),
    createDataImportUpload: build.mutation<
      DataImportRecord,
      { importType: string; file: File; dryRun?: boolean }
    >({
      query: ({ importType, file, dryRun }) => {
        const form = new FormData();
        form.append('data_import[import_type]', importType);
        form.append('data_import[source_type]', 'csv');
        form.append('data_import[csv_file]', file);
        if (typeof dryRun !== 'undefined') {
          form.append('data_import[options][dry_run]', String(!!dryRun));
        }
        return {
          url: '/data_imports',
          method: 'POST',
          data: form,
        };
      },
      invalidatesTags: ['DataImport'],
    }),
    createDataImport: build.mutation<
      DataImportRecord,
      CreateCsvPayload | CreateGooglePayload
    >({
      query: (body) => ({
        url: '/data_imports',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['DataImport'],
    }),
    getDataImport: build.query<DataImportRecord, string>({
      query: (id) => ({ url: `/data_imports/${id}` }),
      providesTags: (_result, _e, id) => [{ type: 'DataImport' as const, id }],
    }),
    listDataImports: build.query<DataImportRecord[], void>({
      query: () => ({ url: '/data_imports' }),
      providesTags: ['DataImport'],
    }),
  }),
});

export const {
  useGetDataImportTypesQuery,
  useCreateDataImportUploadMutation,
  useCreateDataImportMutation,
  useGetDataImportQuery,
  useListDataImportsQuery,
} = dataImportsApi;
